import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { SubTopic } from '@shapeable/gesda-types';
import { ContentEntityIntro, ContentNode, DottedLine, EntityProvider, HorizonIndicators, NextPageLink } from '@shapeable/ui';
import { HorizonGridLayout } from './horizon-grid-layout';
import { SubTopicAnticipatoryScoresLayout } from './sub-topic-anticipatory-scores-layout';
const cls = classNames('sub-topic-view-layout');

// -------- Types -------->

// -------- Props -------->

export type SubTopicViewLayoutProps = Classable & HasChildren & { 
  entity?: SubTopic;
  animateHorizons?: boolean;
};

export const SubTopicViewLayoutDefaultProps: Omit<SubTopicViewLayoutProps, 'entity'> = {
  animateHorizons: true,
};

// -------- Child Component Props -------->

type ContainerProps = {
  color?: string;
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(4)};

    .shp--content-node {
      a {
        ${theme.TRANSITION('filter')};
        color: ${({ color }: ContainerProps ) => color || theme.COLOR('primary')};

        &:hover {
          filter: brightness(1.2);
        }
      }
    }
  `,
});

const SectionStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
    margin-bottom: ${theme.UNIT(5)};
  `,
});

const IntroStyles = breakpoints({
  base: css`
    font-size: 1em;
    font-weight: 300;
  `,
});

const ContentStyles = breakpoints({
  base: css`
    margin-bottom: ${theme.UNIT(5)};
    font-weight: 300;
  `,
});

const HorizonsStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(8)};
    margin-bottom: ${theme.UNIT(8)};
  `,
});

const HorizonIndicatorsStyles = breakpoints({
  base: css`
    margin-bottom: ${theme.UNIT(4)};
  `,
});

const AnticipationScoresLayoutStyles = breakpoints({
  base: css`
    
  `,
});

const NextLinkStyles = breakpoints({
  base: css`
    margin: ${theme.UNIT(4)} 0;
  `,
});



// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,

  Section: styled.section`${SectionStyles}`,

      Intro: styled(ContentEntityIntro)`${IntroStyles}`,
      Content: styled(ContentNode)`${ContentStyles}`,
      Horizons: styled(HorizonGridLayout)`${HorizonsStyles}`,
        HorizonIndicators: styled(HorizonIndicators)`${HorizonIndicatorsStyles}`,
      AnticipationScoresLayout: styled(SubTopicAnticipatoryScoresLayout)`${AnticipationScoresLayoutStyles}`,

      NextLink: styled(NextPageLink)`${NextLinkStyles}`,
};

export const SubTopicViewLayout: Shapeable.FC<SubTopicViewLayoutProps> = (props) => {
  const { className, children, entity, animateHorizons } = props;

  const { description, horizons = [], anticipationScores, anticipationScoresImage, color } = entity;

  const hasHorizons = !!horizons.length;
  const hasAnticipationScores = !!anticipationScores && !!anticipationScoresImage;
  
  return (
    <EntityProvider value={entity} >
      <My.Container className={cls.name(className)} color={color?.value}>
        <My.Section>
          <My.Intro entity={entity} />
          {
            hasHorizons &&
              <My.Horizons
                items={horizons}
              >
                <My.HorizonIndicators animate={animateHorizons} />
              </My.Horizons>
          }
          <My.Content entity={description} />
          {
            hasAnticipationScores &&
            <My.AnticipationScoresLayout entity={entity} />
          }
        </My.Section>
      </My.Container>
    </EntityProvider>
  )
};

SubTopicViewLayout.defaultProps = SubTopicViewLayoutDefaultProps;
SubTopicViewLayout.cls = cls;